import { FC, useMemo } from 'react';
import { WizardFormData, StepErrors } from '../types';
import RoleEntity from '../../../../../../../../domain/entities/RoleEntity';
import PermissionPreview from '../components/PermissionPreview';
import SearchDropdown from '../../dropDownInput';

interface StepRoleProps {
    formData: WizardFormData;
    errors: StepErrors;
    roles: RoleEntity[];
    onChange: (field: keyof WizardFormData, value: any) => void;
}

/**
 * Step 1 — Role Selection with Permission Preview
 *
 * Renders a role dropdown sorted alphabetically. When a role is selected,
 * displays a PermissionPreview showing the role's modules and permissions.
 *
 * Requirements: 3.1, 3.2, 3.3, 3.4, 3.6
 */
export const Step1_Role: FC<StepRoleProps> = ({ formData, errors, roles, onChange }) => {
    const errorId = 'role-error';
    const hasError = !!errors.role_id;

    // Sort roles alphabetically by name (Requirement 3.1)
    const sortedRoles = useMemo(() => {
        return [...roles].sort((a, b) => a.name.localeCompare(b.name));
    }, [roles]);

    // Format roles as { label, subLabel } for the SearchDropdown component
    const roleDropdownData = useMemo(() => {
        return sortedRoles.map((role) => ({
            label: role.name,
            subLabel: '',
        }));
    }, [sortedRoles]);

    // Find the currently selected role entity
    const selectedRole = useMemo(() => {
        if (!formData.role_id) return null;
        return roles.find((r) => r.id === formData.role_id) ?? null;
    }, [formData.role_id, roles]);

    const handleRoleSelect = (item: { label: string; subLabel: string }, _id: string) => {
        const matched = sortedRoles.find((r) => r.name === item.label);
        if (matched) {
            onChange('role_id', matched.id);
            onChange('roleName', matched.name);
        } else {
            // Cleared selection
            onChange('role_id', null);
            onChange('roleName', '');
        }
    };

    // Requirement 3.6 — empty role list
    if (roles.length === 0) {
        return (
            <div className="step-role">
                <div className="step-role__empty-message" role="status">
                    <p style={{ color: '#666', fontStyle: 'italic' }}>
                        No roles available. Please contact an administrator to configure roles.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="step-role">
            <div className="step-role__field">
                <div className="user_list_title">Select a Role:</div>
                <div
                    className={hasError ? 'step-role__dropdown--invalid' : ''}
                    style={hasError ? { border: '1px solid red', borderRadius: '4px' } : undefined}
                    aria-describedby={hasError ? errorId : undefined}
                >
                    <SearchDropdown
                        data={roleDropdownData}
                        onSelect={handleRoleSelect}
                        selected={formData.roleName}
                        id="role"
                        editable={false}
                    />
                </div>
                {hasError && (
                    <p
                        id={errorId}
                        className="step-role__error"
                        role="alert"
                        style={{ color: 'red', fontSize: '0.85rem', marginTop: '4px' }}
                    >
                        {errors.role_id}
                    </p>
                )}
            </div>

            {/* Requirement 3.2 — show permission preview when a role with modules is selected */}
            {selectedRole && selectedRole.modules && selectedRole.modules.length > 0 && (
                <div className="step-role__permission-preview" style={{ marginTop: '16px' }}>
                    <PermissionPreview modules={selectedRole.modules} />
                </div>
            )}
        </div>
    );
};

export default Step1_Role;
